import { reactive, computed, Ref } from "vue"
import { FetchStatus, QueryResult, QueryStatus } from "./types"

/**
 * Takes a number of queries and aggregates them into a single query object
 * which can be observed, e.g. to determine if all queries have finished loading.
 *
 * @example
 * const aggregator = new QueryAggregator()
 * aggregator.add(getMeterpoints())
 * aggregator.add(getTariffs())
 * aggregator.add(getReadings())
 *
 * isFetching will be false, only if all provided queries have finished
 * const { isFetching, error } = aggregator.getAggregate()
 */

export type QueryOrQueryAggregator<T = unknown> =
  AggregatedQueryProps<T> | QueryResult<T>
export interface IQueryAggregator<T> {
  queries: QueryResult<unknown>[]
  queryAggregate: AggregatedQueryProps<unknown>
  add<U>(query: QueryResult<U>): IQueryAggregator<T>
  setData<U extends T>(data: U): QueryAggregator<U>
  getAggregate(): AggregatedQueryProps<unknown>
}

export interface AggregatedQueryProps<T> extends Partial<
  Omit<QueryResult<T>, "refetch" | "suspense" | "error">
> {
  data: T
  queries: QueryResult<unknown>[]
  error: Error[]
  failureCount: number
  isError: boolean
  isFetching: boolean
  isFetched: boolean
  isLoading: boolean
  isPaused: boolean
  isPlaceholderData: boolean
  isRefetchError: boolean
  isRefetching: boolean
  isStale: boolean
  isSuccess: boolean
  status: QueryStatus
  fetchStatus: FetchStatus
  refetch: () => void
  suspense?: () => Promise<(QueryResult<unknown> | undefined)[]>
}

export class QueryAggregator<T = unknown> implements IQueryAggregator<T> {
  queries: QueryResult<unknown>[]
  queryAggregate: AggregatedQueryProps<T>

  constructor() {
    this.queries = reactive([])
    this.queryAggregate = reactive({
      data: {} as Ref<T>,
      queries: this.queries,
      isFetching: computed(() => this.getIsFetching()),
      error: computed(() => this.getError()),
      failureCount: computed(() => this.getFailureCount()),
      isError: computed(() => this.getIsError()),
      isFetched: computed(() => this.getIsFetched()),
      isLoading: computed(() => this.getIsLoading()),
      isPaused: computed(() => this.getIsPaused()),
      isPlaceholderData: computed(() => this.getIsPlaceholderData()),
      isRefetchError: computed(() => this.getIsRefetchError()),
      isRefetching: computed(() => this.getIsRefetching()),
      isStale: computed(() => this.getIsStale()),
      isSuccess: computed(() => this.getIsSuccess()),
      status: computed(() => this.getStatus()),
      fetchStatus: computed(() => this.getFetchStatus()),
      refetch: () => this.refetch(),
      suspense: () => this.suspense(),
    })
  }

  add<U>(queryOrQueryAggregator: QueryOrQueryAggregator<U>) {
    if ("queries" in queryOrQueryAggregator) {
      this.queries.push(...queryOrQueryAggregator.queries)
    } else {
      this.queries.push(queryOrQueryAggregator)
    }
    return this
  }

  setData<U extends T>(data: U) {
    this.queryAggregate.data = data
    return this as unknown as QueryAggregator<U>
  }

  setDataFunction<U extends T>(dataFunction: () => T) {
    this.queryAggregate.data = computed(() =>
      this.getIsLoading() ? {} : dataFunction(),
    ) as T
    return this as unknown as QueryAggregator<U>
  }

  getAggregate() {
    return this.queryAggregate
  }

  async suspense(): Promise<(QueryResult<unknown> | undefined)[]> {
    return Promise.all(this.queries.map((query) => query.suspense?.()))
  }

  private getIsFetching(): boolean {
    return this.queries.some((q) => q.isFetching)
  }

  private getError(): Error[] {
    return this.queries.filter((q) => q.error).map((q) => q.error as Error)
  }

  private getFailureCount(): number {
    return this.queries.reduce((sum, q) => sum + q.failureCount, 0)
  }

  private getIsError(): boolean {
    return this.queries.some((q) => q.isError)
  }

  private getIsFetched(): boolean {
    return this.queries.every((q) => q.isFetched)
  }

  private getIsLoading(): boolean {
    return this.queries.some((q) => q.isLoading)
  }

  private getIsPaused(): boolean {
    return this.queries.every((q) => q.isPaused)
  }

  private getIsPlaceholderData(): boolean {
    return this.queries.every((q) => q.isPlaceholderData)
  }

  private getIsRefetchError(): boolean {
    return this.queries.some((q) => q.isRefetchError)
  }

  private getIsRefetching(): boolean {
    return this.queries.some((q) => q.isRefetching)
  }

  private getIsStale(): boolean {
    return this.queries.every((q) => q.isStale)
  }

  private getIsSuccess(): boolean {
    return this.queries.every((q) => q.isSuccess)
  }

  private getStatus(): QueryStatus {
    if (this.queries.some((q) => q.status === "error")) {
      return "error"
    }

    if (this.queries.some((q) => q.status === "pending")) {
      return "pending"
    }

    return "success"
  }

  private getFetchStatus(): FetchStatus {
    if (this.queries.some((q) => q.fetchStatus === "fetching")) {
      return "fetching"
    }

    if (this.queries.some((q) => q.fetchStatus === "paused")) {
      return "paused"
    }

    return "idle"
  }

  private refetch(): void {
    this.queries.forEach((q) => q.refetch())
  }
}
